import React from 'react';
import styled from 'styled-components';
import {COLORS, SIZES, Image, ImageBadge, IconBadge, Typography} from '@fupa/fupa-uikit';
import {object} from 'prop-types';

const Rotator = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  border-radius: ${SIZES['34']};
  background-color: white;
`;

const Wrapper = styled.div`
  position: relative;
  height: 7.75rem;
  min-width: ${SIZES['88']};
  @keyframes rotation {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(359deg);
    }
  }

  &:hover {
    ${Rotator} {
      background: ${COLORS.fupaPrimary};
      background: linear-gradient(90deg, ${COLORS.fupaPrimary} 0%, ${COLORS.fupaSecondary} 100%);
      animation: rotation 1s infinite linear;
    }
  }
`;

const TopElevenPlayerContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${SIZES['8']} 0;
  min-width: ${SIZES['88']};
`;

const PlayerData = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: center;
`;

const CustomPosition = styled(Typography)`
  &&& {
    color: ${(props) => (props.dark ? COLORS.black : COLORS.white)};
    line-height: ${SIZES['16']};
  }
`;

// This one is needed to enable centered text and overflow: hidden
const NameWrapper = styled.div`
  width: inherit;
  display: flex;
  justify-content: center;
  overflow: hidden;
  white-space: nowrap;
`;

const TopElevenName = styled(Typography)`
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: ${SIZES['20']};
  padding-top: ${SIZES['4']};
  &&& {
    color: ${(props) => (props.dark ? COLORS.black : COLORS.white)};
  }
`;

const ProfilePhoto = styled.div`
  position: relative;
`;

const Link = styled.a`
  display: block;
  text-decoration: none;

  &:focus,
  &:hover,
  &:visited,
  &:link,
  &:active {
    text-decoration: none;
  }
`;

const ImageWrapper = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  height: ${SIZES['68']};
  width: ${SIZES['68']};
  background-color: white;
  border-radius: ${SIZES['34']};

  div:first-child {
    z-index: 1;
  }
`;

const Svg = styled.svg`
  fill: ${COLORS.grey};
  margin: auto;
  width: 1em;
  height: 1em;
  display: inline-block;
  font-size: ${SIZES[16]};
  flex-shrink: 0;
`;

const TopelevenPlayer = ({player, substitute}) => {
  const setImageAlt = (imageSrc, altText) => {
    if (!imageSrc.description) {
      imageSrc.description = altText;
    }
    return imageSrc;
  };

  const checkImage = (
    <Image
      entity='player'
      src={setImageAlt(player.player.image, player.player.name)}
      circle={true}
      width={64}
      height={64}
      eager={true}
      ratio='1:1'
    />
  );

  const checkClubImage =
    player.player.club && player.player.club.image ? (
      <ImageBadge value={setImageAlt(player.player.club.image, player.player.club.name)} position={1} fit={true} />
    ) : (
      <IconBadge
        value={
          <Svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'>
            <path d='M22.29 3.98c-4.65-.44-8.45-2.92-9.7-3.83a.75.75 0 0 0-.88 0c-1.25.91-5.05 3.39-9.7 3.83-.56.05-.99.51-1 1.08-.06 3.67.53 16.13 10.86 18.91.18.04.37.04.56 0C22.75 21.19 23.34 8.73 23.29 5.06c-.01-.57-.44-1.03-1-1.08z' />
          </Svg>
        }
        position={1}
      />
    );

  const getName = (playerName) => {
    const names = playerName.split(' ');
    const firstName = names[0].charAt(0);
    const lastName = names.slice(-1);
    return names.length > 1 ? firstName + '. ' + lastName : lastName;
  };

  return (
    <Wrapper>
      <Link href={`${process.env.LINK_URL}${player.player.linkUrl}`}>
        <TopElevenPlayerContainer>
          <ProfilePhoto>
            {checkClubImage}
            <ImageWrapper>
              {checkImage}
              <Rotator />
            </ImageWrapper>
          </ProfilePhoto>
          <PlayerData>
            <NameWrapper>
              <TopElevenName variant='body-strong' dark={substitute}>
                {getName(player.player.name)}
              </TopElevenName>
            </NameWrapper>
            {!substitute ? <CustomPosition variant='caption1'>{player.position}</CustomPosition> : null}
          </PlayerData>
        </TopElevenPlayerContainer>
      </Link>
    </Wrapper>
  );
};

TopelevenPlayer.propTypes = {
  player: object.isRequired,
};

export {TopelevenPlayer};
